import React, { useState } from 'react';
import { css } from '@emotion/react';
import { Tag } from '@weave/schema-gen-ts/dist/schemas/tag/shared/v1/models.pb';
import dayjs from 'dayjs';
import { startCase } from 'lodash-es';
import { getCallRecording } from '@frontend/api-phone-calls';
import { TagsUtils } from '@frontend/api-tag';
import { AdaptoActions } from '@frontend/adapto-actions';
import { Actions } from '@frontend/contact-actions';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { formatPhoneNumber } from '@frontend/phone-numbers';
import { CachedAudioScrubber } from '@frontend/phone-ui-widgets';
import { Photos } from '@frontend/photos';
import { useScopedAppFlagStore } from '@frontend/scope';
import { useContactPanelShallowStore } from '@frontend/shared';
import { useSlidePanelShallowStore } from '@frontend/slide-panel';
import { theme } from '@frontend/theme';
import { Avatar, Chip, Text, TextLink } from '@frontend/design-system';
import { RecentCallsTagSelector } from '../hooks/recent-calls-tag-selector';
import { recentCallsTrackingId } from '../pendo-tracking';
import { RecentCallRecord } from '../types';
import { getOfficeUser, getStatusChipColor } from '../utils';

export const ContactSection = ({
  contact,
  locationId,
  isMarked = false,
  children,
}: {
  contact: {
    firstName: string;
    lastName: string;
    profileName: string;
    id: string;
    number: string;
    defaultName: string;
  };
  locationId: string;
  isMarked?: boolean;
  children: React.ReactNode;
}) => {
  const { firstName, lastName, profileName, id: personId, number, defaultName } = contact;
  const { setPersonId } = useContactPanelShallowStore('setPersonId', 'personId');
  const { setShow } = useSlidePanelShallowStore('setShow');

  return (
    <div
      css={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: theme.spacing(1),
      }}
    >
      <div
        style={{
          display: 'grid',
          gridTemplateAreas: '"img name" "img number"',
          gridTemplateColumns: 'auto 1fr',
          columnGap: theme.spacing(1),
          alignItems: 'center',
          alignContent: 'center',
        }}
      >
        <Photos.ContactProfilePhoto
          css={{ gridArea: 'img' }}
          locationId={locationId}
          name={profileName}
          personId={personId}
          disableClick={!personId}
        >
          {!!isMarked && <Avatar.Dot variant='primary' />}
        </Photos.ContactProfilePhoto>
        {(firstName || lastName) && (
          <div
            css={{ gridArea: 'name' }}
            onClick={() => {
              if (!!personId) {
                setPersonId(personId, undefined, locationId);
                setShow(true, 'contact');
              }
            }}
          >
            {!!firstName && (
              <Text as='span' size='medium' weight='bold'>
                {firstName}
              </Text>
            )}
            {!!lastName && (
              <Text as='span' size='medium'>
                {' '}
                {lastName}
              </Text>
            )}
          </div>
        )}
        {!firstName && !lastName && (
          <Text
            css={{ gridArea: 'name' }}
            weight='bold'
            size='medium'
            onClick={() => {
              if (!!personId) {
                setPersonId(personId, undefined, locationId);
                setShow(true, 'contact');
              }
            }}
          >
            {profileName || defaultName}
          </Text>
        )}
        <Text css={{ gridArea: 'number' }} size='medium' color='light'>
          {number}
        </Text>
      </div>
      {children}
    </div>
  );
};

export const ContactActions = ({
  contact,
  locationId,
}: {
  contact: { name: string; id: string; number: string };
  locationId: string;
}) => {
  const { t } = useTranslation('calls');
  return (
    <section
      css={css`
        display: flex;
        justify-content: center;
        gap: ${theme.spacing(1)};
      `}
    >
      <AdaptoActions enableCloseEvent iconColor='inherit' variant='icon-buttons' css={{ display: 'flex' }}>
        <Actions.Call
          label={t(`Call ${contact.name}`)}
          context={{ personId: contact.id, phoneNumber: contact.number, locationId }}
          trackingIdSuffix={recentCallsTrackingId({ context: 'panel::call-contact' })}
        />
        <Actions.Message
          label={t(`Text ${contact.name}`)}
          context={{ personId: contact.id, personPhone: contact.number, threadGroupId: locationId }}
          trackingIdSuffix={recentCallsTrackingId({ context: 'panel::message-contact' })}
        />
      </AdaptoActions>
    </section>
  );
};

export const CallRecordingSection = ({
  row,
  callRecordingPath,
}: {
  row: RecentCallRecord;
  callRecordingPath: string;
}) => {
  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const hasPrivateCommsFlag = getFeatureFlagValue('apply-phone-number-user-access-restriction');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(2), padding: theme.spacing(1) }}>
      <CachedAudioScrubber
        persistPlaybackRate
        trackingId='nwx-phones-call-details-call-recording-audioScrubber'
        filePath={callRecordingPath}
        queryFn={
          hasPrivateCommsFlag
            ? async () => {
                try {
                  if (!row.channelId || !row.locationId) {
                    throw new Error('Missing channelId or locationId');
                  }

                  const res = await getCallRecording({
                    channelId: row.channelId,
                    locationId: row.locationId,
                    token: row.downloadCallRecordingToken,
                  });
                  if (res.signedUrl) {
                    return fetch(res.signedUrl);
                  } else {
                    throw new Error('No signed URL available');
                  }
                } catch (error) {
                  console.error('Error fetching call recording:', error);
                  throw error;
                }
              }
            : undefined
        }
        mediaId={row?.channelId || ''}
      />
    </div>
  );
};

const ListRow = ({ label, children }: { label: string; children?: React.ReactNode }) => {
  return (
    <li
      style={{
        display: 'flex',
        justifyContent: 'space-between',
      }}
    >
      <Text size='large' css={{ color: theme.colors.neutral70 }}>
        {label}
      </Text>
      {children}
    </li>
  );
};

export const CallDetailsSection = ({ row, locationName }: { row: RecentCallRecord; locationName: string }) => {
  const { t } = useTranslation('calls');
  const { startedAt, direction, status = '', locationId, callerNumber, dialedNumber } = row;
  const statusChipColor = getStatusChipColor(status);

  return (
    <ul style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(2), padding: theme.spacing(2) }}>
      <Text weight='bold'>{t('Call Details')}</Text>
      <ListRow label={t('Location')}>
        {!!locationId && <Chip.Location>{locationName}</Chip.Location>}
        {!locationId && <span>{'--'}</span>}
      </ListRow>

      <ListRow label={t('Time')}>
        <Text>{dayjs(startedAt).format('MMM DD YYYY, hh:mm A')}</Text>
      </ListRow>

      <ListRow label={t('Result')}>
        {direction === 'outbound' && <span>{'--'}</span>}
        {direction === 'inbound' && (
          <Chip isResponsive variant={statusChipColor}>
            {startCase(status)}
          </Chip>
        )}
      </ListRow>

      <Text weight='bold'>{t('Call Path Overview')}</Text>

      <ListRow label={t('Office Number')}>
        <Text size='large'>{formatPhoneNumber(direction === 'outbound' ? callerNumber : dialedNumber)}</Text>
      </ListRow>

      <ListRow label={t('Office User')}>
        <Text size='large'>{getOfficeUser(row)}</Text>
      </ListRow>
    </ul>
  );
};

export const TagsSection = ({
  tags,
  onApplyTag,
  onRemoveTag,
}: {
  tags: Tag[];
  onApplyTag?: (tag: Tag) => void;
  onRemoveTag?: (tag: Tag) => void;
}) => {
  const { t } = useTranslation('calls');
  const [isEditing, setIsEditing] = useState(false);

  return (
    <div style={{ display: 'flex', gap: theme.spacing(2), flexDirection: 'column' }}>
      {tags.length === 0 && isEditing && (
        <div style={{ padding: theme.spacing(1) }}>
          <RecentCallsTagSelector
            onClose={() => {
              setIsEditing(false);
            }}
            onSelect={() => {
              setIsEditing(false);
            }}
            onApplyTag={onApplyTag}
            existingTagIds={tags.map((t) => t.id)}
          />
        </div>
      )}
      {tags.length === 0 && !isEditing && <Text color='subdued'>{t('No tags added')}</Text>}
      {tags.length > 0 && (
        <div style={{ display: 'flex', gap: theme.spacing(1), padding: theme.spacing(1), flexWrap: 'wrap' }}>
          {tags.length > 0 &&
            tags.map((tag) => (
              <Chip.Tag
                isRemovable
                onRemoveClick={() => {
                  onRemoveTag?.(tag);
                }}
                key={tag.id}
                color={TagsUtils.convertStringToTagColor(tag.color)}
              >
                {tag.name}
              </Chip.Tag>
            ))}
          {isEditing && (
            <RecentCallsTagSelector
              onClose={() => {
                setIsEditing(false);
              }}
              onSelect={() => {
                setIsEditing(false);
              }}
              existingTagIds={tags.map((t) => t.id)}
              onApplyTag={onApplyTag}
            />
          )}
        </div>
      )}
      <TextLink
        disabled={isEditing}
        weight='bold'
        style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: theme.spacing(1) }}
        onClick={() => {
          setIsEditing(true);
        }}
        trackingId={recentCallsTrackingId({ context: 'panel::add-tag' })}
      >
        <Icon name='label' />
        {t('Add Tag')}
      </TextLink>
    </div>
  );
};
